import type { Lead } from '@up2stay/up2promo-api-client';
import { Badge } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { CalendarDays, Tag } from 'lucide-react';

import {
  getLeadCardLabelChips,
  type LeadCardLabelMaps,
} from '@/core/components/leads/lead-card-label-chips';
import { HighlightMatchText } from '@/core/components/ui/highlight-match-text';

export type { LeadCardLabelMaps };
export { getLeadCardLabelChips };

type LeadCardLabelsProps = {
  lead: Lead;
  maps?: LeadCardLabelMaps;
  /** Tighter layout for board cards */
  compact?: boolean;
  /** Free-text search — matching tags are surfaced and highlighted */
  searchQuery?: string;
  className?: string;
};

function prioritizeMatchingLabels(labels: string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return labels;
  return [...labels].sort((a, b) => {
    const aMatch = a.toLowerCase().includes(q) ? 0 : 1;
    const bMatch = b.toLowerCase().includes(q) ? 0 : 1;
    return aMatch - bMatch;
  });
}

export function LeadCardLabels({
  lead,
  maps,
  compact = false,
  searchQuery,
  className,
}: LeadCardLabelsProps) {
  const { events, tags } = getLeadCardLabelChips(lead, maps);
  const orderedTags = prioritizeMatchingLabels(tags, searchQuery ?? '');

  if (events.length === 0 && orderedTags.length === 0) return null;

  const maxEvents = compact ? 1 : 3;
  const maxTags = compact ? 1 : 3;
  const visibleEvents = events.slice(0, maxEvents);
  const visibleTags = orderedTags.slice(0, maxTags);
  const more =
    Math.max(0, events.length - visibleEvents.length) +
    Math.max(0, orderedTags.length - visibleTags.length);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1',
        compact && 'gap-0.5',
        className,
      )}
    >
      {visibleEvents.map((name) => (
        <Badge
          key={`event:${name}`}
          variant="secondary"
          className="h-5 max-w-[10rem] gap-1 truncate border-transparent px-1.5 text-xs font-normal"
          title={name}
        >
          <CalendarDays className="h-3 w-3 shrink-0 opacity-70" />
          <span className="truncate">{name}</span>
        </Badge>
      ))}
      {visibleTags.map((tag) => (
        <Badge
          key={`tag:${tag}`}
          variant="outline"
          className="h-5 max-w-[10rem] gap-1 truncate px-1.5 text-xs font-normal"
          title={tag}
        >
          <Tag className="h-3 w-3 shrink-0 opacity-70" />
          <HighlightMatchText
            text={tag}
            query={searchQuery}
            className="truncate"
          />
        </Badge>
      ))}
      {more > 0 ? (
        <span className="text-xs text-muted-foreground">+{more}</span>
      ) : null}
    </div>
  );
}
